import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { FaEye, FaEyeSlash, FaLeaf } from 'react-icons/fa';

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,600;0,700;1,400;1,600&family=Lato:wght@300;400;700;900&display=swap');

  :root {
    --russian-red:  #CD0302;
    --sunburst:     #F0C400;
    --edam:         #F5D19D;
    --citrusy:      #F88702;
    --green:        #4F7E17;
    --green-light:  #6aad1f;
    --edam-light:   #fdf3e3;
    --edam-dark:    #e8c070;
    --soil:         #3d2b1f;
    --cream:        #fffdf5;
    --fog:          #f7f2ea;
  }

  .lp-root {
    font-family: 'Lato', sans-serif;
    min-height: 100vh;
    background:
      radial-gradient(ellipse at 0% 0%,   rgba(79,126,23,0.13) 0%, transparent 50%),
      radial-gradient(ellipse at 100% 100%, rgba(240,196,0,0.10) 0%, transparent 50%),
      radial-gradient(ellipse at 100% 0%,   rgba(248,135,2,0.07) 0%, transparent 40%),
      var(--cream);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 40px 16px;
    color: var(--soil);
  }

  .lp-card-wrap {
    width: 100%;
    max-width: 420px;
  }

  /* ── Brand Header ── */
  .lp-brand {
    text-align: center;
    margin-bottom: 28px;
  }

  .lp-brand-icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 52px; height: 52px;
    background: linear-gradient(135deg, var(--green), var(--green-light));
    border-radius: 16px;
    margin-bottom: 12px;
    box-shadow: 0 4px 16px rgba(79,126,23,0.3);
  }

  .lp-brand-title {
    font-family: 'Playfair Display', Georgia, serif;
    font-size: clamp(1.6rem, 4vw, 2.1rem);
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    line-height: 1.2;
    margin-bottom: 4px;
  }

  .lp-brand-title span { color: var(--green); }

  .lp-brand-sub {
    font-size: 0.8rem;
    color: #9a8878;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    font-weight: 700;
  }

  /* ── Main Card ── */
  .lp-card {
    background: #fff;
    border: 1.5px solid var(--edam);
    border-radius: 20px;
    padding: 32px 28px;
    box-shadow: 0 4px 28px rgba(79,126,23,0.10), 0 1px 4px rgba(0,0,0,0.04);
    position: relative;
    overflow: hidden;
  }

  .lp-card::before {
    content: '';
    position: absolute;
    top: 0; left: 0; right: 0;
    height: 4px;
    background: linear-gradient(90deg, var(--green), var(--citrusy), var(--sunburst));
    border-radius: 20px 20px 0 0;
  }

  /* ── Mode Title ── */
  .lp-mode-title {
    font-family: 'Playfair Display', serif;
    font-size: 1.4rem;
    font-weight: 700;
    font-style: italic;
    color: var(--soil);
    margin-bottom: 22px;
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .lp-mode-dot {
    width: 8px; height: 8px;
    border-radius: 50%;
    background: var(--green);
    flex-shrink: 0;
  }

  /* ── Form elements ── */
  .lp-field { margin-bottom: 16px; }

  .lp-label {
    display: block;
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--soil);
    margin-bottom: 6px;
    letter-spacing: 0.03em;
  }

  .lp-input {
    width: 100%;
    padding: 11px 14px;
    border: 1.5px solid var(--edam-dark);
    border-radius: 10px;
    background: var(--fog);
    font-family: 'Lato', sans-serif;
    font-size: 0.92rem;
    color: var(--soil);
    outline: none;
    transition: border-color 0.2s, box-shadow 0.2s, background 0.2s;
    box-sizing: border-box;
  }

  .lp-input:focus {
    border-color: var(--green);
    background: #fff;
    box-shadow: 0 0 0 3px rgba(79,126,23,0.12);
  }

  .lp-input:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .lp-input-wrap {
    position: relative;
  }

  .lp-input-wrap .lp-input {
    padding-right: 42px;
  }

  .lp-eye-btn {
    position: absolute;
    right: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: none;
    border: none;
    cursor: pointer;
    color: #aaa;
    transition: color 0.15s;
    display: flex;
    align-items: center;
    padding: 0;
  }

  .lp-eye-btn:hover { color: var(--green); }

  /* ── Primary Button ── */
  .lp-btn-primary {
    width: 100%;
    padding: 13px 20px;
    background: linear-gradient(135deg, var(--green) 0%, var(--green-light) 100%);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-family: 'Playfair Display', serif;
    font-size: 1rem;
    font-weight: 700;
    letter-spacing: 0.02em;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s, filter 0.2s;
    box-shadow: 0 4px 16px rgba(79,126,23,0.32);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .lp-btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 7px 22px rgba(79,126,23,0.38);
    filter: brightness(1.05);
  }

  .lp-btn-primary:disabled {
    background: linear-gradient(135deg, #c8c8c8, #b0b0b0);
    color: #777;
    cursor: not-allowed;
    box-shadow: none;
    transform: none;
  }

  /* ── Secondary Button ── */
  .lp-btn-secondary {
    width: 100%;
    padding: 12px 20px;
    background: var(--fog);
    color: var(--soil);
    border: 1.5px solid var(--edam-dark);
    border-radius: 12px;
    font-family: 'Lato', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: background 0.2s, border-color 0.2s, transform 0.15s;
  }

  .lp-btn-secondary:hover:not(:disabled) {
    background: var(--edam-light);
    border-color: var(--edam-dark);
    transform: translateY(-1px);
  }

  .lp-btn-secondary:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Orange Button (auto-password) ── */
  .lp-btn-orange {
    width: 100%;
    padding: 12px 20px;
    background: linear-gradient(135deg, var(--citrusy) 0%, #e07000 100%);
    color: #fff;
    border: none;
    border-radius: 12px;
    font-family: 'Lato', sans-serif;
    font-size: 0.9rem;
    font-weight: 700;
    cursor: pointer;
    transition: transform 0.2s, box-shadow 0.2s;
    box-shadow: 0 3px 14px rgba(248,135,2,0.3);
  }

  .lp-btn-orange:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(248,135,2,0.4);
  }

  .lp-btn-orange:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  /* ── Divider ── */
  .lp-divider {
    display: flex;
    align-items: center;
    gap: 12px;
    margin: 18px 0;
  }

  .lp-divider-line {
    flex: 1;
    height: 1px;
    background: var(--edam-dark);
  }

  .lp-divider-text {
    font-size: 0.78rem;
    color: #aaa;
    font-weight: 700;
    letter-spacing: 0.06em;
  }

  /* ── Section separator ── */
  .lp-section-sep {
    border: none;
    border-top: 1px dashed var(--edam-dark);
    margin: 22px 0;
  }

  .lp-section-label {
    font-size: 0.8rem;
    font-weight: 700;
    color: var(--green);
    letter-spacing: 0.08em;
    text-transform: uppercase;
    margin-bottom: 14px;
    display: flex;
    align-items: center;
    gap: 6px;
  }

  /* ── Alerts ── */
  .lp-alert {
    border-radius: 10px;
    padding: 12px 14px;
    font-size: 0.86rem;
    margin-top: 14px;
    font-weight: 600;
    line-height: 1.5;
  }

  .lp-alert.error {
    background: rgba(205,3,2,0.06);
    border: 1.5px solid rgba(205,3,2,0.22);
    color: var(--russian-red);
  }

  .lp-alert.success {
    background: rgba(79,126,23,0.07);
    border: 1.5px solid rgba(79,126,23,0.22);
    color: var(--green);
  }

  .lp-alert.loading {
    background: rgba(240,196,0,0.10);
    border: 1.5px solid rgba(240,196,0,0.35);
    color: #8a6800;
  }

  /* ── Legal note ── */
  .lp-legal {
    font-size: 0.75rem;
    color: #aaa;
    line-height: 1.6;
    margin-top: 14px;
  }

  /* ── Forgot link ── */
  .lp-forgot {
    background: none;
    border: none;
    padding: 0;
    font-size: 0.82rem;
    font-weight: 700;
    color: var(--citrusy);
    cursor: pointer;
    margin-top: 10px;
    transition: color 0.15s;
    text-decoration: underline;
    text-underline-offset: 3px;
  }

  .lp-forgot:hover { color: var(--russian-red); }

  /* ── Bottom Card ── */
  .lp-bottom-card {
    margin-top: 18px;
    background: #fff;
    border: 1.5px solid var(--edam);
    border-radius: 16px;
    padding: 20px 24px;
    box-shadow: 0 2px 12px rgba(79,126,23,0.07);
  }

  .lp-new-label {
    text-align: center;
    font-size: 0.82rem;
    color: #aaa;
    font-weight: 700;
    letter-spacing: 0.06em;
    margin-bottom: 14px;
    position: relative;
  }

  .lp-new-label::before,
  .lp-new-label::after {
    content: '';
    position: absolute;
    top: 50%;
    width: 28%;
    height: 1px;
    background: var(--edam-dark);
  }

  .lp-new-label::before { left: 0; }
  .lp-new-label::after  { right: 0; }

  /* ── Footer links ── */
  .lp-footer {
    margin-top: 22px;
    text-align: center;
  }

  .lp-footer-links {
    display: flex;
    justify-content: center;
    gap: 18px;
    flex-wrap: wrap;
    margin-bottom: 10px;
  }

  .lp-footer-link {
    font-size: 0.75rem;
    color: var(--citrusy);
    text-decoration: underline;
    text-underline-offset: 2px;
    transition: color 0.15s;
    cursor: pointer;
  }

  .lp-footer-link:hover { color: var(--russian-red); }

  .lp-footer-copy {
    font-size: 0.72rem;
    color: #bbb;
    margin-top: 6px;
  }

  /* ── Spinner ── */
  @keyframes lp-spin { to { transform: rotate(360deg); } }
  .lp-spinner {
    width: 16px; height: 16px;
    border: 2px solid rgba(255,255,255,0.4);
    border-top-color: #fff;
    border-radius: 50%;
    animation: lp-spin 0.7s linear infinite;
    flex-shrink: 0;
  }

  .lp-spinner-dark {
    width: 16px; height: 16px;
    border: 2px solid rgba(79,126,23,0.25);
    border-top-color: var(--green);
    border-radius: 50%;
    animation: lp-spin 0.7s linear infinite;
    flex-shrink: 0;
  }
`;

function LoginPage() {
  const [phoneNumber, setPhoneNumber]     = useState('');
  const [otpCode, setOtpCode]             = useState('');
  const [password, setPassword]           = useState('');
  const [newPassword, setNewPassword]     = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);

  const [showOtpInput, setShowOtpInput]           = useState(false);
  const [showPasswordInput, setShowPasswordInput] = useState(false);

  const [isLoginMode, setIsLoginMode]                 = useState(true);
  const [isForgotPasswordMode, setIsForgotPasswordMode] = useState(false);

  const [error, setError]               = useState('');
  const [success, setSuccess]           = useState('');
  const [isLoading, setIsLoading]       = useState(false);
  const [resettingPassword, setResettingPassword] = useState(false);

  const navigate = useNavigate();
  const { login, getAuthHeaders, storeUserInfo } = useAuth();

  const togglePasswordVisibility    = () => setShowPassword(!showPassword);
  const toggleNewPasswordVisibility = () => setShowNewPassword(!showNewPassword);

  /* ── All handlers unchanged ── */
  const handleLogin = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    if (!password.trim()) { setError('Password cannot be empty.'); setIsLoading(false); return; }
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/login', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber, password }),
      });
      const data = await response.json();
      if (response.ok) { setSuccess(data.message); login(data.access_token, data.user_info); navigate('/'); }
      else setError(data.error || 'Login failed.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleSendOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setShowOtpInput(false); setShowPasswordInput(false); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/send_otp', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ phone_number: phoneNumber }),
      });
      const data = await response.json();
      if (response.ok) { setSuccess(data.message + " Please wait for your OTP."); setShowOtpInput(true); }
      else setError(data.error || 'Failed to send OTP.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    if (!otpCode.trim()) { setError('OTP code cannot be empty.'); setIsLoading(false); return; }
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/verify_otp', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ phone_number: phoneNumber, otp_code: otpCode }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message);
        if (data.is_registered) { login(data.access_token, data.user_info); navigate('/'); }
        else setShowPasswordInput(true);
      } else setError(data.error || 'OTP verification failed.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleCompleteRegistration = async (e, autoGenerate = false) => {
    e.preventDefault(); setError(''); setSuccess(''); setIsLoading(true);
    let endpoint = autoGenerate
      ? 'https://bazar-wala-main-server-974485829900.asia-south1.run.app/register_with_auto_password'
      : 'https://bazar-wala-main-server-974485829900.asia-south1.run.app/complete_registration';
    let bodyData = { phone_number: phoneNumber };
    if (!autoGenerate) {
      if (!password.trim()) { setError('Password cannot be empty.'); setIsLoading(false); return; }
      bodyData.password = password;
    }
    try {
      const response = await fetch(endpoint, { method: 'POST', headers: getAuthHeaders(), body: JSON.stringify(bodyData) });
      const data = await response.json();
      if (response.ok) { setSuccess(data.message); login(data.access_token, data.user_info); navigate('/'); }
      else setError(data.error || 'Failed to complete registration.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleForgotPasswordSendOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setNewPassword(''); setIsLoading(true);
    if (!/^\d{11}$/.test(phoneNumber)) { setError('Please enter a valid 11-digit mobile number.'); setIsLoading(false); return; }
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/forgot_password_send_otp', {
        method: 'POST', headers: getAuthHeaders(), body: JSON.stringify({ phone_number: phoneNumber }),
      });
      const data = await response.json();
      if (response.ok) { setSuccess(data.message + " Please wait for your OTP."); setShowOtpInput(true); setShowPasswordInput(false); }
      else setError(data.error || 'Failed to send OTP.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setIsLoading(false); }
  };

  const handleResetPasswordVerifyOtp = async (e) => {
    e.preventDefault(); setError(''); setSuccess(''); setResettingPassword(true);
    if (!otpCode.trim() || !newPassword.trim()) { setError('Both OTP code and new password are required.'); setResettingPassword(false); return; }
    try {
      const response = await fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/reset_password_verify_otp', {
        method: 'POST', headers: getAuthHeaders(),
        body: JSON.stringify({ phone_number: phoneNumber, otp_code: otpCode, new_password: newPassword }),
      });
      const data = await response.json();
      if (response.ok) {
        setSuccess(data.message + " You can now login with your new password.");
        setIsForgotPasswordMode(false); setIsLoginMode(true);
        setPhoneNumber(''); setOtpCode(''); setPassword(''); setNewPassword('');
        setShowOtpInput(false); setShowPasswordInput(false);
      } else setError(data.error || 'Password reset failed.');
    } catch (err) { setError('Unable to connect to server.'); }
    finally { setResettingPassword(false); }
  };

  const handleToggleMode = () => {
    setIsLoginMode(!isLoginMode); setIsForgotPasswordMode(false);
    setPhoneNumber(''); setOtpCode(''); setPassword(''); setNewPassword('');
    setShowOtpInput(false); setShowPasswordInput(false);
    setError(''); setSuccess(''); setIsLoading(false); setResettingPassword(false);
  };

  const handleToggleForgotPasswordMode = () => {
    setIsForgotPasswordMode(true); setIsLoginMode(false);
    setPhoneNumber(''); setOtpCode(''); setPassword(''); setNewPassword('');
    setShowOtpInput(false); setShowPasswordInput(false);
    setError(''); setSuccess(''); setIsLoading(false); setResettingPassword(false);
  };

  const modeTitle = isLoginMode
    ? 'Welcome back'
    : isForgotPasswordMode
    ? 'Reset Password'
    : 'Create Account';

  return (
    <>
      <style>{styles}</style>
      <div className="lp-root">
        <div className="lp-card-wrap">


          {/* ── Main Card ── */}
          <div className="lp-card">
            <div className="lp-mode-title">
              <span className="lp-mode-dot" />
              {modeTitle}
            </div>

            {/* ── LOGIN MODE ── */}
            {isLoginMode && (
              <form onSubmit={handleLogin}>
                <div className="lp-field">
                  <label className="lp-label" htmlFor="phone">📱 Mobile Number</label>
                  <input id="phone" type="tel" className="lp-input"
                    placeholder="11-digit mobile number"
                    value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                    required disabled={isLoading} />
                </div>

                <div className="lp-field">
                  <label className="lp-label" htmlFor="password">🔒 Password</label>
                  <div className="lp-input-wrap">
                    <input id="password" type={showPassword ? 'text' : 'password'}
                      className="lp-input" placeholder="Your password"
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      required disabled={isLoading} />
                    <button type="button" className="lp-eye-btn" onClick={togglePasswordVisibility}>
                      {showPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                    </button>
                  </div>
                </div>

                <button type="submit" className="lp-btn-primary" disabled={isLoading}>
                  {isLoading ? <><div className="lp-spinner" /> Signing in...</> : '→ Sign In'}
                </button>

                <p className="lp-legal">
                  By continuing, you agree to our Conditions of Use and Privacy Notice.
                </p>

                <div style={{ marginTop: '12px' }}>
                  <button type="button" className="lp-forgot" onClick={handleToggleForgotPasswordMode}>
                    Forgot your password?
                  </button>
                </div>
              </form>
            )}

            {/* ── REGISTER MODE ── */}
            {!isLoginMode && !isForgotPasswordMode && (
              <>
                <form onSubmit={handleSendOtp}>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="phone-register">📱 Mobile Number</label>
                    <input id="phone-register" type="tel" className="lp-input"
                      placeholder="11-digit mobile number"
                      value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                      required disabled={showOtpInput || showPasswordInput || isLoading} />
                  </div>
                  <button type="submit" className="lp-btn-primary"
                    disabled={showOtpInput || showPasswordInput || isLoading}>
                    {isLoading ? <><div className="lp-spinner" /> Sending...</> : '→ Send OTP'}
                  </button>
                </form>

                {showOtpInput && (
                  <form onSubmit={handleVerifyOtp}>
                    <hr className="lp-section-sep" />
                    <p className="lp-section-label">
                      <span>✉</span> Verify OTP
                    </p>
                    <div className="lp-field">
                      <label className="lp-label" htmlFor="otp">OTP Code</label>
                      <input id="otp" type="text" className="lp-input"
                        placeholder="Enter OTP code"
                        value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                        required disabled={showPasswordInput || isLoading} />
                    </div>
                    <button type="submit" className="lp-btn-primary"
                      disabled={showPasswordInput || isLoading}>
                      {isLoading ? <><div className="lp-spinner" /> Verifying...</> : '→ Verify OTP'}
                    </button>
                  </form>
                )}

                {showPasswordInput && (
                  <div>
                    <hr className="lp-section-sep" />
                    <p className="lp-section-label"><span>🔑</span> Complete Registration</p>

                    <div className="lp-field">
                      <label className="lp-label" htmlFor="set-password">Set Password</label>
                      <div className="lp-input-wrap">
                        <input id="set-password" type={showNewPassword ? 'text' : 'password'}
                          className="lp-input" placeholder="Choose a password"
                          value={password} onChange={(e) => setPassword(e.target.value)}
                          required disabled={isLoading} />
                        <button type="button" className="lp-eye-btn" onClick={toggleNewPasswordVisibility}>
                          {showNewPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                        </button>
                      </div>
                    </div>

                    <button type="button" className="lp-btn-primary" disabled={isLoading}
                      onClick={(e) => handleCompleteRegistration(e, false)}>
                      {isLoading ? <><div className="lp-spinner" /> Registering...</> : '→ Register with Password'}
                    </button>

                    <div className="lp-divider">
                      <div className="lp-divider-line" />
                      <span className="lp-divider-text">OR</span>
                      <div className="lp-divider-line" />
                    </div>

                    <button type="button" className="lp-btn-orange" disabled={isLoading}
                      onClick={(e) => handleCompleteRegistration(e, true)}>
                      {isLoading ? <><div className="lp-spinner" /> Registering...</> : '⚡ Register with Auto Password'}
                    </button>
                  </div>
                )}
              </>
            )}

            {/* ── FORGOT PASSWORD MODE ── */}
            {isForgotPasswordMode && (
              <>
                <form onSubmit={handleForgotPasswordSendOtp}>
                  <div className="lp-field">
                    <label className="lp-label" htmlFor="phone-reset">📱 Mobile Number</label>
                    <input id="phone-reset" type="tel" className="lp-input"
                      placeholder="11-digit mobile number"
                      value={phoneNumber} onChange={(e) => setPhoneNumber(e.target.value)}
                      required disabled={showOtpInput || isLoading} />
                  </div>
                  <button type="submit" className="lp-btn-primary"
                    disabled={showOtpInput || isLoading}>
                    {isLoading ? <><div className="lp-spinner" /> Sending OTP...</> : '→ Send OTP'}
                  </button>
                </form>

                {showOtpInput && (
                  <form onSubmit={handleResetPasswordVerifyOtp}>
                    <hr className="lp-section-sep" />
                    <p className="lp-section-label"><span>🔑</span> Set New Password</p>

                    <div className="lp-field">
                      <label className="lp-label" htmlFor="otp-reset">OTP Code</label>
                      <input id="otp-reset" type="text" className="lp-input"
                        placeholder="Your OTP code"
                        value={otpCode} onChange={(e) => setOtpCode(e.target.value)}
                        required disabled={resettingPassword} />
                    </div>

                    <div className="lp-field">
                      <label className="lp-label" htmlFor="new-password">New Password</label>
                      <div className="lp-input-wrap">
                        <input id="new-password" type={showNewPassword ? 'text' : 'password'}
                          className="lp-input" placeholder="Enter new password"
                          value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                          required disabled={resettingPassword} />
                        <button type="button" className="lp-eye-btn" onClick={toggleNewPasswordVisibility}>
                          {showNewPassword ? <FaEyeSlash size={15} /> : <FaEye size={15} />}
                        </button>
                      </div>
                    </div>

                    <button type="submit" className="lp-btn-primary" disabled={resettingPassword}>
                      {resettingPassword
                        ? <><div className="lp-spinner" /> Resetting...</>
                        : '→ Reset Password'}
                    </button>
                  </form>
                )}
              </>
            )}

            {/* ── Alerts ── */}
            {error   && <div className="lp-alert error">✗ {error}</div>}
            {success && <div className="lp-alert success">✓ {success}</div>}
            {(isLoading || resettingPassword) && (
              <div className="lp-alert loading" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <div className="lp-spinner-dark" /> Please wait...
              </div>
            )}
          </div>

          {/* ── Bottom Toggle Card ── */}
          <div className="lp-bottom-card">
            <p className="lp-new-label">New to our store?</p>
            <button className="lp-btn-secondary" onClick={handleToggleMode}>
              {isLoginMode && !isForgotPasswordMode
                ? '🌱 Create your account'
                : '← Sign in to your account'}
            </button>
          </div>

          {/* ── Footer ── */}
          <div className="lp-footer">
            <div className="lp-footer-links">
              <a href="#" className="lp-footer-link">Conditions of Use</a>
              <a href="#" className="lp-footer-link">Privacy Notice</a>
              <a href="#" className="lp-footer-link">Help</a>
            </div>
            <p className="lp-footer-copy">© 2019-2026, Bazarwala or its affiliates</p>
          </div>

        </div>
      </div>
    </>
  );
}

export default LoginPage;